import React from 'react';
import { Modal, View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { CUSTOMER_CARD_ENABLED } from '../constants/featureFlags';
import Button3D from './Button3D';

interface PaymentModalProps {
  visible: boolean;
  selectedMethod: 'CASH' | 'CARD' | 'TRANSFER';
  onSelect: (method: 'CASH' | 'CARD' | 'TRANSFER') => void;
  onClose: () => void;
  driverBankAccounts?: any[];
}

export default function PaymentModal({ visible, selectedMethod, onSelect, onClose, driverBankAccounts }: PaymentModalProps) {
  return (
    <Modal
      transparent
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.bottomSheet}>
          <Text style={styles.title}>Método de pago</Text>
          <Text style={styles.subtitle}>Selecciona cómo quieres pagar este viaje.</Text>
          
          <TouchableOpacity 
            style={[styles.optionRow, selectedMethod === 'CASH' && styles.optionSelected]}
            onPress={() => onSelect('CASH')}
          >
            <View style={[styles.iconContainer, { backgroundColor: '#4CAF5015' }]}>
              <Ionicons name="cash" size={24} color="#4CAF50" />
            </View>
            <Text style={styles.optionText}>Efectivo</Text>
            {selectedMethod === 'CASH' && <Ionicons name="checkmark-circle" size={24} color={Colors.primary} />}
          </TouchableOpacity>

          {/* Pago de VIAJE por tarjeta: fuera por regla de negocio (viajes en
              efectivo o con el link propio del chofer/MÍO, no por el Stripe de
              la app). Controlado por CUSTOMER_CARD_ENABLED. */}
          {CUSTOMER_CARD_ENABLED && (
            <TouchableOpacity
              style={[styles.optionRow, selectedMethod === 'CARD' && styles.optionSelected]}
              onPress={() => onSelect('CARD')}
            >
              <View style={[styles.iconContainer, { backgroundColor: '#2196F315' }]}>
                <Ionicons name="card" size={24} color="#2196F3" />
              </View>
              <Text style={styles.optionText}>Tarjeta de Crédito/Débito</Text>
              {selectedMethod === 'CARD' && <Ionicons name="checkmark-circle" size={24} color={Colors.primary} />}
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[styles.optionRow, selectedMethod === 'TRANSFER' && styles.optionSelected]}
            onPress={() => onSelect('TRANSFER')}
          >
            <View style={[styles.iconContainer, { backgroundColor: '#FF980015' }]}>
              <Ionicons name="business" size={24} color="#FF9800" />
            </View>
            <Text style={styles.optionText}>Transferencia Bancaria</Text>
            {selectedMethod === 'TRANSFER' && <Ionicons name="checkmark-circle" size={24} color={Colors.primary} />}
          </TouchableOpacity>

          {selectedMethod === 'TRANSFER' && driverBankAccounts && driverBankAccounts.length > 0 && (
            <View style={styles.bankAccountsContainer}>
              <Text style={styles.bankAccountsTitle}>Cuentas del Chofer:</Text>
              {driverBankAccounts.map((acc: any, index: number) => (
                <View key={index} style={styles.bankAccountItem}>
                  <Text style={styles.bankName}>{acc.bankName}</Text>
                  {/* Un Toke es un telefono, no una cuenta de ahorros: sin esto
                      el pasajero leia «TOKE - 8295420292». Mismo criterio que en
                      driver-bank-accounts.tsx y en el cierre de pago del mapa. */}
                  <Text style={styles.accountDetails}>
                    {acc.accountType === 'TOKE'
                      ? `Teléfono - ${acc.accountNumber}`
                      : `${acc.accountType} - ${acc.accountNumber}`}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {selectedMethod === 'TRANSFER' && (!driverBankAccounts || driverBankAccounts.length === 0) && (
            <View style={styles.bankAccountsContainer}>
              <Text style={{ color: Colors.textSecondary, fontSize: 13, fontStyle: 'italic', textAlign: 'center' }}>
                Este chofer no ha registrado cuentas bancarias.
              </Text>
            </View>
          )}

          <Button3D 
            title="Confirmar" 
            onPress={onClose} 
            color={Colors.primary} 
            style={styles.confirmButton}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  bottomSheet: {
    backgroundColor: Colors.card,
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    padding: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 24,
  },
  title: {
    fontSize: SIZES.large,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: SIZES.font,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.border,
    marginBottom: 12,
  },
  optionSelected: {
    borderColor: Colors.primary,
    backgroundColor: `${Colors.primary}05`,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  optionText: {
    flex: 1,
    fontSize: SIZES.medium,
    fontWeight: '600',
    color: Colors.text,
  },
  confirmButton: {
    marginTop: 10,
  },
  bankAccountsContainer: {
    backgroundColor: '#F9F9F9',
    padding: 15,
    borderRadius: 12,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#EEEEEE',
  },
  bankAccountsTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 10,
  },
  bankAccountItem: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  bankName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: Colors.primary,
  },
  accountDetails: {
    fontSize: 13,
    color: Colors.textSecondary,
    marginTop: 2,
  }
});
